import { useCallback, useState } from 'react';
import { useApi, fetchApiRef } from '@backstage/frontend-plugin-api';
import {
  CHECK_IMPORT_BATCH_SIZE,
  type CheckImportResult,
} from '@internal/backstage-plugin-pulse-common';
import { checkImportResultSchema } from '../schemas/checks';
import {
  invalidateCache,
  PULSE_CACHE_KEYS,
  PULSE_CACHE_KEY_PREFIXES,
} from './cache';

const errorFromResponse = async (response: Response): Promise<Error> => {
  try {
    const data = await response.json();
    return new Error(data.error ?? `Check import failed: ${response.status}`);
  } catch {
    return new Error(`Check import failed: ${response.status}`);
  }
};

type FailedCheckImportBatch = {
  offset: number;
  size: number;
};

export class CheckImportBatchError extends Error {
  readonly partialResult: CheckImportResult;
  readonly failedBatch: FailedCheckImportBatch;

  constructor(
    message: string,
    partialResult: CheckImportResult,
    failedBatch: FailedCheckImportBatch,
  ) {
    super(message);
    this.name = 'CheckImportBatchError';
    this.partialResult = partialResult;
    this.failedBatch = failedBatch;
  }
}

export const isCheckImportBatchError = (
  error: unknown,
): error is CheckImportBatchError =>
  error instanceof Error &&
  error.name === 'CheckImportBatchError' &&
  'partialResult' in error &&
  'failedBatch' in error;

/** Imports parsed YAML check definitions as one partial-success batch. */
export const useImportChecks = () => {
  const { fetch } = useApi(fetchApiRef);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const clearError = useCallback((): void => setError(null), []);

  const importChecks = useCallback(
    async (checks: unknown[]): Promise<CheckImportResult> => {
      setLoading(true);
      setError(null);
      const result: CheckImportResult = {
        total: 0,
        imported: [],
        skipped: [],
        failed: [],
      };
      try {
        for (
          let offset = 0;
          offset < Math.max(checks.length, 1);
          offset += CHECK_IMPORT_BATCH_SIZE
        ) {
          const batch = checks.slice(offset, offset + CHECK_IMPORT_BATCH_SIZE);
          const response = await fetch('plugin://pulse/checks/import', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ checks: batch }),
          });
          if (!response.ok) {
            const responseError = await errorFromResponse(response);
            throw new CheckImportBatchError(responseError.message, result, {
              offset,
              size: batch.length,
            });
          }

          const batchResult = checkImportResultSchema.parse(
            await response.json(),
          );
          result.total += batchResult.total;
          result.imported.push(...batchResult.imported);
          result.skipped.push(...batchResult.skipped);
          result.failed.push(...batchResult.failed);
        }

        return result;
      } catch (error_) {
        const importError =
          error_ instanceof Error ? error_ : new Error('Check import failed');
        setError(importError);
        throw importError;
      } finally {
        if (result.imported.length > 0) {
          invalidateCache(PULSE_CACHE_KEYS.checks);
          invalidateCache(PULSE_CACHE_KEY_PREFIXES.trackChecks);
          invalidateCache(PULSE_CACHE_KEY_PREFIXES.insights);
          invalidateCache(PULSE_CACHE_KEY_PREFIXES.integrationHealth);
        }
        setLoading(false);
      }
    },
    [fetch],
  );

  return { importChecks, loading, error, clearError };
};
